import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn } from 'components/create-dialog/useCreateDraft';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import Loader from 'components/Loader';
import { useMemo, useState } from 'react';
import { useGetOfficeByIdQuery } from 'store/api/offices';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { OfficeForm } from 'types/offices';
import { useClassifiers } from 'utils/useClassifiers';
import { officeDtoToForm } from './officeFormMapping';
import OfficePageForm from './OfficePageForm';

const USED_CLASSIFIERS = ['COUNTRY'];

/** The add form loaded with what a draft holds, read as the office's edit page reads it. */
const OfficeDraftAdd = ({ draftId }: { draftId: number }) => {
    const [draft, setDraft] = useState<{ initialData: OfficeForm; customFields: CustomField[] }>();
    const { data: office } = useGetOfficeByIdQuery(String(draftId), { refetchOnMountOrArgChange: true });
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const {
        customFields,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({ entityType: EntityType.OFFICE, fields: office?.customFields });

    // Frozen once loaded: a refetch must not reset what the user is typing.
    if (!draft && office && !isLoadingCustomFieldData) {
        setDraft({ initialData: officeDtoToForm(office, initialCustomFieldsObject), customFields });
    }

    return draft && !isLoadingClassifiers ? (
        <OfficePageForm
            type="add"
            initialData={draft.initialData}
            classifiers={classifiers}
            entityCustomFields={draft.customFields}
        />
    ) : (
        <Loader />
    );
};

const OfficeBlankAdd = () => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });

    const { initialCustomFieldsObject, isLoading: isLoadingCustomFieldData } = useCustomFields({
        entityType: EntityType.OFFICE,
    });

    // Set initial form data
    const initialData = useMemo<OfficeForm | undefined>(() => {
        if (isLoadingCustomFieldData || !classifiers) {
            return undefined;
        }
        return {
            name: '',
            company: null,
            companies: [],
            emailAddress: '',
            invoicingEmailAddress: '',
            deadlineEventEmailAddress: '',
            invoiceAbbreviation: '',
            phoneNumber: '',
            abbreviation: '',
            address: null,
            localAddress: null,
            isActive: true,
            invoiceTemplates: [],
            invoiceSpecTemplates: [],
            defaultLocalCountryInvoiceTemplate: null,
            defaultForeignCountryInvoiceTemplate: null,
            defaultLocalCountryInvoiceSpecTemplate: null,
            defaultForeignCountryInvoiceSpecTemplate: null,
            ...initialCustomFieldsObject,
        };
    }, [classifiers, initialCustomFieldsObject, isLoadingCustomFieldData]);

    if (isLoadingClassifiers || initialData === undefined || isLoadingCustomFieldData) {
        return <Loader />;
    }

    return <OfficePageForm type="add" initialData={initialData} classifiers={classifiers} />;
};

/** The office create; its URL with `?draft=<id>` edits that draft instead of a blank office. */
const OfficePageAdd = () => {
    const draftId = draftIdIn(useCreateRoute().location.search);
    return draftId === null ? <OfficeBlankAdd /> : <OfficeDraftAdd key={draftId} draftId={draftId} />;
};

export default OfficePageAdd;
